import type { RoleKey } from '@/access/roles'

/**
 * The pure role-mapping module (PX-W6.1, spec §6.3): backend signals → the ten
 * prototype RoleKeys, in ONE total function.
 *
 * Feeds `canAccess` (via the session slice's `roleKey`) — never bypasses it
 * (memory `role-access-is-the-product-claim`).
 *
 * Purity (I-4): no I/O, no store reads, no randomness, no clock. Total over
 * every {@link RoleSignals}: every one of the ten RoleKeys is reachable, and a
 * signal set matching no named arm derives `consultant` (least privilege,
 * I-13) — including the zero-org registrant, who never reaches role-guarded
 * surfaces because the bootstrap redirect (AuthGuard) precedes role gating.
 *
 * `RoleKey` comes from `@/access/roles` — PX-W6.6 re-homed the ladder out of
 * the demo module, so no product file reaches into `src/demo/` for it (I-2).
 */

/** The backend signals the mapping reads (spec §6.3). */
export interface RoleSignals {
  /** `MongoUser.role` — the platform's explicit named role for this user. */
  userRole: string | null
  /** `MongoUser.memberRole` (enum name) — the team-page display role. */
  memberRole: string | null
  /** `MongoUser.organisations` — org memberships; role compared lower-cased. */
  organisations: readonly { orgId: string; role: string }[]
  /**
   * The project-membership side, fed by later waves (W6.3); `null` = unknown,
   * treated as firm side.
   */
  projectSide: 'firm' | 'client' | 'platform' | null
  /** The caller's role on the project (client-side only, later waves). */
  projectRole: string | null
}

/** Normalise a string signal: trim + lower-case (comparison rule, §6.3). */
const normaliseRole = (value: string | null): string => (value ?? '').trim().toLowerCase()

/**
 * Priority 4–8 arms: a named firm role. `userRole` wins outright; `memberRole`
 * counts only when `userRole` is ABSENT (a null userRole means the platform
 * has not stated a role; an explicit one always outranks the display role).
 */
const NAMED_FIRM_ROLES: Readonly<Record<string, RoleKey>> = {
  'project-manager': 'project-manager',
  manager: 'manager',
  hr: 'hr',
  expert: 'expert',
  analyst: 'analyst'
}

/** Org roles that satisfy the org-admin arm (priority 9). */
const ADMIN_ORG_ROLES: ReadonlySet<string> = new Set(['owner', 'admin'])

/**
 * The mapped RoleKeys that carry org-admin standing — the priority-1
 * (`super-admin`) and priority-9 (`admin`) arms of {@link deriveRoleKey}.
 * The single shared definition of "this lens may use an admin-gated
 * affordance" (e.g. the budget revision form), so a surface never re-states
 * the role list. Destination gating stays in `roleAccess.ts`; this is the
 * affordance-level lens set only.
 */
export const ORG_ADMIN_ROLE_KEYS: readonly RoleKey[] = ['admin', 'super-admin']

/** Project roles that make a client-side principal a `client-manager` (priority 2). */
const CLIENT_MANAGER_PROJECT_ROLES: ReadonlySet<string> = new Set([
  'manager',
  'sponsor',
  'client-manager'
])

const namedFirmRole = (role: string): RoleKey | null => NAMED_FIRM_ROLES[role] ?? null

/**
 * Derive the session's RoleKey from backend signals. First matching arm wins
 * (spec §6.3 table, priorities 1–10). Pure and total.
 */
export function deriveRoleKey(signals: RoleSignals): RoleKey {
  const userRole = normaliseRole(signals.userRole)
  const memberRole = normaliseRole(signals.memberRole)

  // Priority 1 — the platform principal. Super admin spans implementer FIRMS
  // (memory super-admin-spans-firms), which is why it outranks everything,
  // including the client-side arms.
  if (
    userRole === 'super-admin' ||
    memberRole === 'super-admin' ||
    signals.projectSide === 'platform'
  ) {
    return 'super-admin'
  }

  // Priorities 2–3 — the client side, decided by the project membership.
  if (signals.projectSide === 'client') {
    return CLIENT_MANAGER_PROJECT_ROLES.has(normaliseRole(signals.projectRole))
      ? 'client-manager'
      : 'client-employee'
  }

  // Priorities 4–8 — named firm roles. An explicit `userRole` outranks the
  // org-admin arm (D5: an owner who is also a project manager keeps their
  // working lens). `memberRole` counts only when `userRole` is absent.
  if (userRole !== '') {
    const named = namedFirmRole(userRole)
    if (named !== null) return named
  } else {
    const named = namedFirmRole(memberRole)
    if (named !== null) return named
  }

  // Priority 9 — org admin: owner/admin of ANY organisation.
  const adminOfAny = (signals.organisations ?? []).some((membership) =>
    ADMIN_ORG_ROLES.has(normaliseRole(membership?.role))
  )
  if (adminOfAny) {
    return 'admin'
  }

  // Priority 10 — default: authenticated firm-side member, or the zero-org
  // registrant (least privilege, I-13).
  return 'consultant'
}

/**
 * The parameterised fixtures the unit test runs over — every RoleKey plus the
 * priority arms (§6.3 row coverage, AC-4). Keep in sync with the table when an
 * arm changes; the test's totality assertion fails if a RoleKey goes missing.
 */
export const ROLE_SIGNALS_FIXTURES: readonly {
  name: string
  signals: RoleSignals
  expected: RoleKey
}[] = [
  {
    name: 'priority 1 — super-admin by explicit userRole',
    signals: {
      userRole: 'super-admin',
      memberRole: null,
      organisations: [],
      projectSide: null,
      projectRole: null
    },
    expected: 'super-admin'
  },
  {
    name: 'priority 1 — super-admin by memberRole when userRole is absent',
    signals: {
      userRole: null,
      memberRole: 'SUPER-ADMIN',
      organisations: [],
      projectSide: null,
      projectRole: null
    },
    expected: 'super-admin'
  },
  {
    name: 'priority 1 beats 2 — the platform side outranks a client-side project role',
    signals: {
      userRole: null,
      memberRole: null,
      organisations: [],
      projectSide: 'platform',
      projectRole: 'manager'
    },
    expected: 'super-admin'
  },
  {
    name: 'priority 2 — client-manager via projectRole manager',
    signals: {
      userRole: null,
      memberRole: null,
      organisations: [],
      projectSide: 'client',
      projectRole: 'manager'
    },
    expected: 'client-manager'
  },
  {
    name: 'priority 2 — client-manager via projectRole sponsor',
    signals: {
      userRole: null,
      memberRole: null,
      organisations: [],
      projectSide: 'client',
      projectRole: 'sponsor'
    },
    expected: 'client-manager'
  },
  {
    name: 'priority 3 — any other client-side project role (null included)',
    signals: {
      userRole: null,
      memberRole: null,
      organisations: [],
      projectSide: 'client',
      projectRole: null
    },
    expected: 'client-employee'
  },
  {
    name: 'priority 4 — project-manager by userRole',
    signals: {
      userRole: 'project-manager',
      memberRole: null,
      organisations: [],
      projectSide: null,
      projectRole: null
    },
    expected: 'project-manager'
  },
  {
    name: 'priority 4 — project-manager by memberRole when userRole is absent',
    signals: {
      userRole: null,
      memberRole: 'Project-Manager',
      organisations: [],
      projectSide: null,
      projectRole: null
    },
    expected: 'project-manager'
  },
  {
    name: 'priority 5 — manager by userRole',
    signals: {
      userRole: 'manager',
      memberRole: null,
      organisations: [],
      projectSide: null,
      projectRole: null
    },
    expected: 'manager'
  },
  {
    name: 'priority 6 — hr by userRole',
    signals: {
      userRole: 'hr',
      memberRole: null,
      organisations: [],
      projectSide: null,
      projectRole: null
    },
    expected: 'hr'
  },
  {
    name: 'priority 7 — expert by memberRole when userRole is absent',
    signals: {
      userRole: null,
      memberRole: 'expert',
      organisations: [],
      projectSide: null,
      projectRole: null
    },
    expected: 'expert'
  },
  {
    name: 'priority 8 — analyst by userRole',
    signals: {
      userRole: 'analyst',
      memberRole: null,
      organisations: [],
      projectSide: null,
      projectRole: null
    },
    expected: 'analyst'
  },
  {
    name: 'priority 9 — admin via an owner org membership',
    signals: {
      userRole: null,
      memberRole: null,
      organisations: [{ orgId: 'perpleks', role: 'owner' }],
      projectSide: null,
      projectRole: null
    },
    expected: 'admin'
  },
  {
    name: 'priority 9 — admin via an admin org membership (case-insensitive)',
    signals: {
      userRole: null,
      memberRole: null,
      organisations: [{ orgId: 'perpleks', role: ' Admin ' }],
      projectSide: null,
      projectRole: null
    },
    expected: 'admin'
  },
  {
    name: 'D5 — a named userRole outranks the org-admin arm (owner + project-manager)',
    signals: {
      userRole: 'project-manager',
      memberRole: null,
      organisations: [{ orgId: 'perpleks', role: 'owner' }],
      projectSide: null,
      projectRole: null
    },
    expected: 'project-manager'
  },
  {
    name: 'priority 10 — a member-only org membership loses to the default',
    signals: {
      userRole: null,
      memberRole: null,
      organisations: [{ orgId: 'perpleks', role: 'member' }],
      projectSide: null,
      projectRole: null
    },
    expected: 'consultant'
  },
  {
    name: 'D4 — the zero-org registrant derives consultant (least privilege)',
    signals: {
      userRole: null,
      memberRole: null,
      organisations: [],
      projectSide: null,
      projectRole: null
    },
    expected: 'consultant'
  },
  {
    name: 'an unknown userRole falls through to the default (no invented lenses)',
    signals: {
      userRole: 'wizard',
      memberRole: null,
      organisations: [],
      projectSide: null,
      projectRole: null
    },
    expected: 'consultant'
  }
]
